import { t } from '@lingui/macro';
import { noddiAsync, URLKeys } from 'noddi-async';
import { quoteItemStatus } from 'noddi-types';
import { NoddiBasicCard, NoddiDialog, NoddiLink } from 'noddi-ui-web';
import { useAuthContext } from 'noddi-web';
import { useState } from 'react';
import { ApiErrorMessageWithTrans } from '../../components/ApiErrorMessageWithTrans';
import ContentWrapper from '../../components/Layouts/ContentWrapper';
import { CUSTOMER_SERVICE_EMAIL } from '../../constants/externalUrls';
import { ConfirmTireOffer } from './ConfirmTireOffer';
import { TireOfferStatusCard } from './TireOfferStatusCard';

// eslint-disable-next-line import/no-default-export
export default function TireOfferHomePage() {
  const { currentUserGroupId: userGroupId } = useAuthContext();
  const [activeTireSetOfferId, setActiveTireSetOfferId] = useState<number | null>(null);

  const {
    isPending: isTireSetOffersPending,
    data: tireSetOffers,
    isFetching: isTireSetOffersFetching,
    error: tireSetOffersError
  } = noddiAsync.useGet({
    type: URLKeys.getCarTireQuotes,
    input: { userGroupIds: [userGroupId!] },
    queryConfig: {
      select(data) {
        // filter out withdrawn and rejected offers
        const filteredData = data.filter(
          (tireSetOffer) =>
            tireSetOffer.status.status !== quoteItemStatus.WITHDRAWN &&
            tireSetOffer.status.status !== quoteItemStatus.REJECTED
        );
        return filteredData.sort((a, b) => {
          if (a.isSelected) {
            return -1;
          }
          if (b.isSelected) {
            return 1;
          }
          return 0;
        });
      }
    }
  });

  if (isTireSetOffersPending || isTireSetOffersFetching) {
    return (
      <ContentWrapper title={t`Tire offer`}>
        <div className='flex flex-col gap-4'>
          {[1, 2, 3, 4, 5].map((index) => (
            <div key={index} className='h-32 animate-pulse rounded-lg bg-darkPurple/30' />
          ))}
        </div>
      </ContentWrapper>
    );
  }

  if (tireSetOffersError) {
    return <ApiErrorMessageWithTrans error={tireSetOffersError} />;
  }

  if (tireSetOffers.length === 0) {
    return (
      <ContentWrapper title={t`Tire offer`}>
        <NoddiBasicCard>
          <span>
            {t`You currently don't have any tire offers - if you would like to get a tire offer, please contact us at`}{' '}
            <NoddiLink variant='link' size='small' className='inline-flex p-0' to={CUSTOMER_SERVICE_EMAIL}>
              hei@noddi.no
            </NoddiLink>
          </span>
        </NoddiBasicCard>
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper title={t`Tire offer`}>
      <div className='flex flex-col gap-4'>
        {tireSetOffers.map((tireSetOffer) => (
          <TireOfferStatusCard
            key={tireSetOffer.id}
            tireSetOffer={tireSetOffer}
            setActiveTireSetOfferId={setActiveTireSetOfferId}
          />
        ))}
      </div>
      <NoddiDialog
        title={t`Select and order tire set`}
        open={!!activeTireSetOfferId}
        onClose={() => setActiveTireSetOfferId(null)}
      >
        {activeTireSetOfferId && (
          <ConfirmTireOffer tireSetOfferId={activeTireSetOfferId} setActiveTireSetOfferId={setActiveTireSetOfferId} />
        )}
      </NoddiDialog>
    </ContentWrapper>
  );
}
